/**
 * THE CANVAS TYPE VOCABULARY — the green canvas's shared language for labels and
 * evidence status.
 *
 * It lives in its own module because more than one surface component draws on the
 * canvas (the working blocks and the Naming Brief deliverable), and the hierarchy
 * is only worth anything if it is IDENTICAL across them. A second copy of these
 * classes is how a hierarchy quietly flattens.
 *
 * THE HIERARCHY. Three deliberate weights, so someone glancing at the canvas can
 * tell interface chrome from a section heading from the agent's actual output —
 * without reading a word. Sizes, colours and spacing are shared; WEIGHT alone
 * carries the structure.
 *
 *   1. CanvasFrameLabel   — extrabold. The interface/status chrome that FRAMES the
 *                           canvas: the four corners (AGENT OUTPUT, LIVE / HOLDING,
 *                           SAY MY NAME / OUTPUT FIELD, UPDATED IN REAL TIME).
 *                           Boldest, because it is the frame, not the content.
 *   2. CanvasSectionLabel — semibold. Section headings INSIDE the canvas
 *                           (ESTABLISHED, IN PROGRESS, MILESTONES, WHAT TO DO NEXT,
 *                           SELECTED NAME, and each block's own title). A clear
 *                           step below the frame.
 *   3. CanvasFieldLabel   — medium. The micro-label naming ONE row of output
 *                           (MEANING AND ORIGIN, DOMAIN .COM). Lightest label rank;
 *                           it belongs to the content.
 *
 * The output itself — every sentence the agent wrote — stays `font-normal`. It is
 * the thing being read, and making it compete would defeat the whole hierarchy.
 *
 * All of them are BLACK on the green canvas: small type and rules on the canvas are
 * never white.
 */
import type { FC, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cn } from '@/app/lib/utils';

const messages = defineMessages({
  statusConfirmed: {
    id: 'studio.evidence.confirmed',
    defaultMessage: 'CONFIRMED',
    description: 'Badge on a research row backed by a source the agent actually read',
  },
  statusConflict: {
    id: 'studio.evidence.conflict',
    defaultMessage: 'CONFLICT',
    description: 'Badge on a research row where something works against the name',
  },
  statusUnresolved: {
    id: 'studio.evidence.unresolved',
    defaultMessage: 'NOT CONFIRMED',
    description: 'Badge on a research row whose check did not complete',
  },
});

export const STATUS_MESSAGES = {
  confirmed: messages.statusConfirmed,
  conflict: messages.statusConflict,
  unresolved: messages.statusUnresolved,
} as const;

export type EvidenceStatus = keyof typeof STATUS_MESSAGES;

export function readStatus(value: unknown): EvidenceStatus | undefined {
  return value === 'confirmed' || value === 'conflict' || value === 'unresolved'
    ? value
    : undefined;
}

/**
 * How a status looks. The three readings must be distinguishable at a glance
 * WITHOUT introducing a second palette onto the green canvas: everything stays in
 * the canvas ink, and the weight of the tag does the work.
 *   confirmed  — quiet outline; this is the normal, expected case
 *   conflict   — solid, filled: the one thing that should stop you
 *   unresolved — dashed outline: deliberately unfinished-looking
 */
export const STATUS_CLASS: Record<EvidenceStatus, string> = {
  confirmed: 'border border-canvas-meta/30 text-canvas-meta/80',
  conflict: 'border border-canvas-ink bg-canvas-ink text-canvas',
  unresolved: 'border border-dashed border-canvas-meta/70 text-canvas-meta',
};

/** The status tag itself: small caps, canvas ink, no new colour. */
export const StatusTag: FC<{ status: EvidenceStatus }> = ({ status }) => {
  const intl = useIntl();
  return (
    <span
      className={cn(
        'rounded-[3px] px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-caps',
        STATUS_CLASS[status],
      )}
    >
      {intl.formatMessage(STATUS_MESSAGES[status])}
    </span>
  );
};

const canvasLabelBase = 'text-[10px] uppercase leading-none tracking-caps text-canvas-meta/70';

/** The three label ranks, in descending weight. Exported so the hierarchy is
 *  asserted in a test rather than trusted to survive future edits by eye. */
export const CANVAS_LABEL_WEIGHT = {
  frame: 'font-extrabold',
  section: 'font-semibold',
  field: 'font-medium',
} as const;

export function canvasLabelClass(rank: keyof typeof CANVAS_LABEL_WEIGHT): string {
  return cn(canvasLabelBase, CANVAS_LABEL_WEIGHT[rank]);
}

/** Rank 1 — the interface chrome framing the canvas. Boldest thing on the canvas. */
export const CanvasFrameLabel: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => <span className={cn(canvasLabelClass('frame'), className)}>{children}</span>;

/** Rank 2 — a section heading inside the canvas. Below the frame, above the rows. */
export const CanvasSectionLabel: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => <span className={cn(canvasLabelClass('section'), className)}>{children}</span>;

/** Rank 3 — the label on a single row of output. */
export const CanvasFieldLabel: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => <span className={cn(canvasLabelClass('field'), className)}>{children}</span>;
